import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { memberIdSchema } from '../identity/legacy-identity-id';

// ─── handled escalations ────────────────────────────────────────────────────
//
// Wire vocabulary for the two staff reads behind the Atlas "Handled
// escalations" section: a keyset list of escalations handled since an instant,
// and per-handler totals over the same window.
//
// The window start travels as an explicit instant. The browser computes it in
// the staff member's own zone (so "today" is their today), and the server only
// bounds it — it holds no staff-timezone state and buckets nothing.
//
// As in the open queue, an item names the escalation, the member by Galapagos
// id only, and when it was raised and handled — never the member's name, the
// reason, the source or the transcript. The handler's display name is staff
// personal data shown on purpose; per-handler totals are an aggregate about
// staff and sit behind a narrower capability on the server.

/** The furthest back a window may start. Thirty days plus slack for "30 days" computed in any zone. */
export const HANDLED_ESCALATIONS_MAX_WINDOW_DAYS = 31;

/**
 * The window start: an ISO-8601 instant carrying its offset (`Z` included). A
 * naive local time is refused, because the server would have to guess whose
 * local time it was. So is anything finer than milliseconds: the server holds
 * the instant as a JS Date, which would silently truncate it and return rows
 * from before the instant asked for.
 */
const sinceSchema = z.iso.datetime({ offset: true }).refine((value) => !/\.\d{4,}/.test(value), {
  message: 'since must not be finer than milliseconds',
});

/**
 * Query params for the handled list. `.strict()`: a REQUEST schema, so an
 * unknown parameter is refused rather than silently ignored (ADR-135 Rule 1).
 */
export const handledEscalationsQuerySchema = z
  .object({
    since: sinceSchema,
    limit: z.string().optional(),
    cursor: z.string().optional(),
  })
  .strict();
export type HandledEscalationsQuery = z.infer<typeof handledEscalationsQuerySchema>;

/**
 * Who handled an escalation. `name` is null when the staff record yields no
 * display name; the client owns the copy for that case. Non-strict, like every
 * response object here (ADR-135 Rule 1).
 */
export const handledEscalationHandlerSchema = z.object({
  name: z.string().min(1).nullable(),
});
export type HandledEscalationHandler = z.infer<typeof handledEscalationHandlerSchema>;

/**
 * One handled escalation. Both instants are UTC with the stored microsecond
 * precision. `handledBy` is null when the escalation carries no handler.
 */
export const handledEscalationItemSchema = z.object({
  escalationId: z.string().uuid(),
  // The shared member-id grammar, for the same reason as the open queue: imported
  // members keep ids with no RFC version guarantee.
  memberId: memberIdSchema,
  raisedAt: z.iso.datetime(),
  handledAt: z.iso.datetime(),
  handledBy: handledEscalationHandlerSchema.nullable(),
});
export type HandledEscalationItem = z.infer<typeof handledEscalationItemSchema>;

/**
 * A keyset page, newest handled first, plus `handledCount`: every escalation
 * handled in the window, whatever page the caller is on — it is what the card
 * shows.
 */
export const handledEscalationsDataSchema = z.object({
  items: z.array(handledEscalationItemSchema),
  nextCursor: z.string().nullable(),
  handledCount: z.number().int().nonnegative(),
});
export type HandledEscalationsData = z.infer<typeof handledEscalationsDataSchema>;

export const handledEscalationsResponseSchema = apiEnvelope(handledEscalationsDataSchema);
export type HandledEscalationsResponse = z.infer<typeof handledEscalationsResponseSchema>;

/** Query params for the per-handler totals. Strict, as above. */
export const handledEscalationsByHandlerQuerySchema = z
  .object({
    since: sinceSchema,
  })
  .strict();
export type HandledEscalationsByHandlerQuery = z.infer<
  typeof handledEscalationsByHandlerQuerySchema
>;

/**
 * One handler's total in the window. The unattributed group — escalations
 * handled with no recorded handler — has `internalUserId: null`, so the totals
 * always sum to the list's `handledCount`.
 */
export const handledEscalationsHandlerTotalSchema = z.object({
  internalUserId: z.string().uuid().nullable(),
  name: z.string().min(1).nullable(),
  handledCount: z.number().int().positive(),
});
export type HandledEscalationsHandlerTotal = z.infer<typeof handledEscalationsHandlerTotalSchema>;

export const handledEscalationsByHandlerDataSchema = z.object({
  total: z.number().int().nonnegative(),
  handlers: z.array(handledEscalationsHandlerTotalSchema),
});
export type HandledEscalationsByHandlerData = z.infer<typeof handledEscalationsByHandlerDataSchema>;

export const handledEscalationsByHandlerResponseSchema = apiEnvelope(
  handledEscalationsByHandlerDataSchema,
);
export type HandledEscalationsByHandlerResponse = z.infer<
  typeof handledEscalationsByHandlerResponseSchema
>;
